import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { take } from 'rxjs';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { EmailConfirmTypes, EmailVerificationOut } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { UserService } from 'src/app/services/user/user.service';
import { isInputError } from 'src/app/shared/functions';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';

@Component({
  templateUrl: './email-signing.component.html',
  styleUrl: './email-signing.component.scss',
  standalone: false,
})
export class EmailSigningComponent implements OnInit {
  public preloader = false;

  public successfullyConfirmedType?: EmailConfirmTypes;
  public confirmFailedType?: EmailConfirmTypes;

  public errored: {
    apiCallFailed: boolean,
    missingToken: boolean,
    reasoned?: string,
  } = {
    apiCallFailed: false,
    missingToken: false,
  };
  public notFoundWithToken = false;

  public constructor(
    private route: ActivatedRoute,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private restApiService: RestApiService,
    private userService: UserService,
    private router: Router,
  ) { }

  public get isRegularWithdrawalConfirmed(): boolean {
    return this.successfullyConfirmedType === EmailConfirmTypes.DIP_REGULAR_WITHDRAWAL || this.successfullyConfirmedType === EmailConfirmTypes.REGULAR_WITHDRAWAL_DRAFT_TO_DIP;
  }

  public get regularWithdrawalConfirmFailed(): boolean {
    return this.confirmFailedType === EmailConfirmTypes.DIP_REGULAR_WITHDRAWAL || this.confirmFailedType === EmailConfirmTypes.REGULAR_WITHDRAWAL_DRAFT_TO_DIP;
  }

  public get isInError(): boolean {
    return !!(this.errored.apiCallFailed || this.errored.missingToken || this.errored.reasoned);
  }

  public ngOnInit(): void {
    this.preloader = true;
    this.route.paramMap.pipe(take(1)).subscribe((params: ParamMap) => {
      const token = params.get('token');
      if(token) {
        this.restApiService.signEmail(token).subscribe({
          error: this.handleConfirmError.bind(this),
          next: this.handleConfirmation.bind(this),
        });
      } else {
        this.preloader = false;
        this.errored.missingToken = true;
        this.toastService.addNotification({
          title: 'toastNotifications.headers.error',
          message: 'emailConfirm.missingToken',
          type: 'warning',
          component: ToastNotificationComponent
        });
      }
    })
  }

  private handleConfirmation(status: EmailVerificationOut): void {
    if(status.reasonWhyNotList.length) {
      this.errored.reasoned = status.reasonWhyNotList[0];
      this.confirmFailedType = status.type;
      this.handleSigningError(status.reasonWhyNotList[0]);
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.success',
        message: 'toastNotifications.messages.'+  (this.isRegularWithdrawalConfirmed ? 'dipWithdrawalConfirmed' : 'fallbackMailConfirm'),
        type: 'success',
        component: ToastNotificationComponent
      });
      this.successfullyConfirmedType = status.type;
      if(this.userService.loggedUser) {
        this.router.navigate(['/money/rent']);
      }
    }
    this.preloader = false;
  }

  private handleSigningError(reasonWhyNot: string) {
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.emailConfirm.' + (this.regularWithdrawalConfirmFailed ? 'regularWithdrawalError' : reasonWhyNot),
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  private handleConfirmError(error: unknown): void {
    if(isInputError(error, 404, { excludeStatusCode: true })) {
      this.toastService.unknownApiError();
      this.errored.apiCallFailed = true;
      this.loggerService.error(typeof error === 'function' ? error() : error);
    } else {
      this.notFoundWithToken = true;
    }
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'emailConfirm.apiErrored',
      type: 'warning',
      component: ToastNotificationComponent
    });
    this.preloader = false;
  }
}
